import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import type { StoreRequisitionView, StoreView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Notice,
  PageHeader,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
  LoadingBlock,
  buttonLinkProps,
} from '@wodi/ui';
import { useState } from 'react';

import { createStoreRequisition, storeRequisitionsQuery } from '../api/procurement.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { screen, TableScroll } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import { LoadFailed, OfflineNote } from '../procurement/common.tsx';
import { documentLabel, documentTag } from '../procurement/format.ts';
import { BackToStore, errorText, newRequestKey, WithStore } from '../stock/common.tsx';
import { readQuantity } from '../stock/format.ts';
import { StockItemPicker } from '../stock/item-picker.tsx';
import styles from '../stock/stock.module.css';
import { toasts } from '../toasts.ts';

// Store requisitions (docs/plans/phase2-procurement.md §5.9): what this store asked other
// stores for, and what others asked of it. Issuing happens on the requisition itself.

const listRoute = getRouteApi('/_app/stores/$storeId/requisitions');
const newRoute = getRouteApi('/_app/stores/$storeId/requisitions/new');

export function StoreRequisitionsPage() {
  const { storeId } = listRoute.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  return (
    <WithStore storeId={storeId}>
      {(store) => (
        <div className={styles.page}>
          <BackToStore storeId={store.id} name={store.name} />
          <PageHeader title={`Store requisitions: ${store.name}`} />
          {session !== null && can(session, 'stock.requisition') ? (
            <div className={styles.actions}>
              <Link
                to="/stores/$storeId/requisitions/new"
                params={{ storeId: store.id }}
                {...buttonLinkProps()}
              >
                Ask another store
              </Link>
            </div>
          ) : null}
          <RequisitionList store={store} direction="of_store" title="Asked of this store" />
          <RequisitionList store={store} direction="from_store" title="Asked by this store" />
        </div>
      )}
    </WithStore>
  );
}

function RequisitionList({
  store,
  direction,
  title,
}: {
  store: StoreView;
  direction: 'from_store' | 'of_store';
  title: string;
}) {
  const reqs = useQuery(storeRequisitionsQuery(store.id, direction));
  return (
    <Section title={title}>
      {reqs.isPending ? <LoadingBlock label="Loading requisitions" rows={3} /> : null}
      {reqs.isError ? (
        <LoadFailed
          error={reqs.error}
          fallback="Store requisitions couldn't be loaded."
          onRetry={() => void reqs.refetch()}
        />
      ) : null}
      {reqs.isSuccess ? (
        <TableScroll>
          <Table flat aria-label={title}>
            <TableHeader>
              <Column isRowHeader>Requisition</Column>
              <Column>{direction === 'of_store' ? 'Asked by' : 'Asked of'}</Column>
              <Column>When</Column>
              <Column numeric>Lines</Column>
              <Column>State</Column>
            </TableHeader>
            <TableBody
              items={reqs.data}
              renderEmptyState={() =>
                direction === 'of_store'
                  ? 'No other store has asked this store for stock.'
                  : 'This store hasn’t asked another store for stock.'
              }
            >
              {(r: StoreRequisitionView) => (
                <Row id={r.id}>
                  <Cell>
                    <Link
                      to="/stores/$storeId/requisitions/$requisitionId"
                      params={{ storeId: store.id, requisitionId: r.id }}
                    >
                      <span className="wodi-id">{r.number}</span>
                    </Link>
                  </Cell>
                  <Cell>
                    {direction === 'of_store' ? r.requestingStore.name : r.supplyingStore.name}
                  </Cell>
                  <Cell>{formatInstant(r.requestedAt)}</Cell>
                  <Cell numeric>{r.lines.length}</Cell>
                  <Cell>
                    <Tag state={documentTag(r.status)}>{documentLabel(r.status)}</Tag>
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      ) : null}
    </Section>
  );
}

interface AskLine {
  key: string;
  itemId: string;
  itemName: string;
  unit: string;
  quantityText: string;
}

export function NewStoreRequisitionPage() {
  const { storeId } = newRoute.useParams();
  return (
    <WithStore storeId={storeId}>
      {(store, stores) => <AskForm store={store} stores={stores} />}
    </WithStore>
  );
}

function AskForm({ store, stores }: { store: StoreView; stores: StoreView[] }) {
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const others = stores.filter((s) => s.id !== store.id);
  const [supplyingId, setSupplyingId] = useState<string | null>(
    others.find((s) => s.kind === 'main')?.id ?? others[0]?.id ?? null,
  );
  const [lines, setLines] = useState<AskLine[]>([]);
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const [requestKey] = useState(newRequestKey);
  const ask = useMutation({
    mutationFn: () =>
      createStoreRequisition({
        clientRequestId: requestKey,
        requestingStoreId: store.id,
        supplyingStoreId: supplyingId ?? '',
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        lines: lines.map((l) => ({
          itemId: l.itemId,
          quantity: readQuantity(l.quantityText) ?? 0,
        })),
      }),
    onSuccess: async (req) => {
      await queryClient.invalidateQueries({ queryKey: ['stock'] });
      toasts.show({ title: 'Stock requested', description: req.number });
      void navigate({
        to: '/stores/$storeId/requisitions/$requisitionId',
        params: { storeId: store.id, requisitionId: req.id },
      });
    },
  });
  const valid =
    supplyingId !== null &&
    lines.length > 0 &&
    lines.every((l) => (readQuantity(l.quantityText) ?? 0) > 0);
  return (
    <div className={styles.page}>
      <BackToStore storeId={store.id} name={store.name} />
      <PageHeader title={`Ask another store for stock for ${store.name}`} />
      {others.length === 0 ? (
        <Notice tone="info">This facility has no other store to ask. Add one in Settings.</Notice>
      ) : (
        <form
          className={`${styles.wideForm} ${screen.panel}`}
          onSubmit={(e) => {
            e.preventDefault();
            setTried(true);
            if (valid) ask.mutate();
          }}
        >
          <Select
            label="Ask"
            options={others.map((s) => ({ id: s.id, label: s.name }))}
            value={supplyingId}
            onChange={(v) => {
              setSupplyingId(String(v));
            }}
          />
          <StockItemPicker
            label="Add an item"
            onSelect={(item) => {
              if (lines.some((l) => l.itemId === item.itemId)) return;
              setLines((all) => [
                ...all,
                {
                  key: newRequestKey(),
                  itemId: item.itemId,
                  itemName: item.name,
                  unit: item.stock?.baseUnit.display ?? '',
                  quantityText: '',
                },
              ]);
            }}
          />
          {lines.length === 0 ? (
            <p className={styles.meta}>No lines yet. Search for each item you need.</p>
          ) : null}
          {lines.map((line) => (
            <div key={line.key} className={styles.row}>
              <TextField
                label={`${line.itemName}: quantity (${line.unit})`}
                inputMode="numeric"
                value={line.quantityText}
                onChange={(v) => {
                  setLines((all) =>
                    all.map((l) => (l.key === line.key ? { ...l, quantityText: v } : l)),
                  );
                }}
                {...(tried && (readQuantity(line.quantityText) ?? 0) === 0
                  ? { errorMessage: 'Enter a whole number.' }
                  : {})}
              />
              <Button
                variant="quiet"
                onPress={() => {
                  setLines((all) => all.filter((l) => l.key !== line.key));
                }}
              >
                Remove
              </Button>
            </div>
          ))}
          <TextArea label="Note (optional)" value={note} onChange={setNote} />
          {ask.error === null ? null : (
            <Notice tone="error">
              {errorText(ask.error, "The request wasn't sent. Try again.")}
            </Notice>
          )}
          <OfflineNote what="Asking for stock" />
          <div className={styles.actions}>
            <Button
              type="submit"
              variant="primary"
              isDisabled={!online}
              isPending={ask.isPending}
              pendingLabel="Sending…"
            >
              Ask for stock
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
